// רשימת פרשות השנה עם תאריכים עבריים ולועזיים
export interface TorahPortion {
  id: string;
  name: string;
  hebrewName: string;
  hebrewDate: string; // תאריך עברי
  gregorianDate: string; // תאריך לועזי
  year: number; // שנת הלוח העברי
}

// רשימת חגים
export interface Holiday {
  id: string;
  name: string;
  hebrewName: string;
  hebrewDate: string; // תאריך עברי
  gregorianDate: string; // תאריך לועזי
  year: number; // שנת הלוח העברי
  hasSixthAliyah: boolean; // האם יש עליה שישית (יום כיפור)
}

// סוגי תפילות
export interface PrayerType {
  id: string;
  name: string;
  hebrewName: string;
  services: Service[];
}

// סוגי מכירות/שירותים
export interface Service {
  id: string;
  name: string;
  hebrewName: string;
  suggestedPrice: number; // מחיר מוצע
}

// פונקציה ליצירת פרשות השנה
const generateTorahPortions = (year: number): TorahPortion[] => {
  // פרשה כללית לחובות ישנים
  const generalPortion = {
    id: `general-${year}`,
    name: 'General',
    hebrewName: 'כללי',
    hebrewDate: 'כללי',
    gregorianDate: '2025-01-01',
    year: year
  };

  // נתונים בסיסיים - ניתן להרחיב בעתיד
  const basePortions = [
    { id: 'bereshit', name: 'Bereshit', hebrewName: 'בראשית', month: 1, day: 22 },
    { id: 'noach', name: 'Noach', hebrewName: 'נח', month: 1, day: 29 },
    { id: 'lech_lecha', name: 'Lech Lecha', hebrewName: 'לך לך', month: 2, day: 6 },
    { id: 'vayera', name: 'Vayera', hebrewName: 'וירא', month: 2, day: 13 },
    { id: 'chayei_sarah', name: 'Chayei Sarah', hebrewName: 'חיי שרה', month: 2, day: 20 },
    { id: 'toledot', name: 'Toledot', hebrewName: 'תולדות', month: 2, day: 27 },
    { id: 'vayetze', name: 'Vayetze', hebrewName: 'ויצא', month: 3, day: 4 },
    { id: 'vayishlach', name: 'Vayishlach', hebrewName: 'וישלח', month: 3, day: 11 },
    { id: 'vayeshev', name: 'Vayeshev', hebrewName: 'וישב', month: 3, day: 18 },
    { id: 'miketz', name: 'Miketz', hebrewName: 'מקץ', month: 3, day: 25 },
    { id: 'vayigash', name: 'Vayigash', hebrewName: 'ויגש', month: 4, day: 2 },
    { id: 'vayechi', name: 'Vayechi', hebrewName: 'ויחי', month: 4, day: 9 },
    { id: 'shemot', name: 'Shemot', hebrewName: 'שמות', month: 4, day: 16 },
    { id: 'vaera', name: 'Vaera', hebrewName: 'וארא', month: 4, day: 23 },
    { id: 'bo', name: 'Bo', hebrewName: 'בא', month: 5, day: 1 },
    { id: 'beshalach', name: 'Beshalach', hebrewName: 'בשלח', month: 5, day: 8 },
    { id: 'yitro', name: 'Yitro', hebrewName: 'יתרו', month: 5, day: 15 },
    { id: 'mishpatim', name: 'Mishpatim', hebrewName: 'משפטים', month: 5, day: 22 },
    { id: 'terumah', name: 'Terumah', hebrewName: 'תרומה', month: 5, day: 29 },
    { id: 'tetzaveh', name: 'Tetzaveh', hebrewName: 'תצוה', month: 6, day: 7 },
    { id: 'ki_tisa', name: 'Ki Tisa', hebrewName: 'כי תשא', month: 6, day: 14 },
    { id: 'vayakhel', name: 'Vayakhel', hebrewName: 'ויקהל', month: 6, day: 21 },
    { id: 'pekudei', name: 'Pekudei', hebrewName: 'פקודי', month: 6, day: 28 },
    { id: 'vayikra', name: 'Vayikra', hebrewName: 'ויקרא', month: 7, day: 5 },
    { id: 'tzav', name: 'Tzav', hebrewName: 'צו', month: 7, day: 12 },
    { id: 'shmini', name: 'Shmini', hebrewName: 'שמיני', month: 7, day: 19 },
    { id: 'tazria', name: 'Tazria', hebrewName: 'תזריע', month: 7, day: 26 },
    { id: 'metzora', name: 'Metzora', hebrewName: 'מצורע', month: 8, day: 3 },
    { id: 'acharei_mot', name: 'Acharei Mot', hebrewName: 'אחרי מות', month: 8, day: 10 },
    { id: 'kedoshim', name: 'Kedoshim', hebrewName: 'קדושים', month: 8, day: 17 },
    { id: 'emor', name: 'Emor', hebrewName: 'אמור', month: 8, day: 24 },
    { id: 'behar', name: 'Behar', hebrewName: 'בהר', month: 9, day: 1 },
    { id: 'bechukotai', name: 'Bechukotai', hebrewName: 'בחוקותי', month: 9, day: 8 },
    { id: 'bamidbar', name: 'Bamidbar', hebrewName: 'במדבר', month: 9, day: 15 },
    { id: 'naso', name: 'Naso', hebrewName: 'נשא', month: 9, day: 22 },
    { id: 'behaalotecha', name: 'Behaalotecha', hebrewName: 'בהעלותך', month: 9, day: 29 },
    { id: 'shlach', name: 'Shlach', hebrewName: 'שלח', month: 10, day: 6 },
    { id: 'korach', name: 'Korach', hebrewName: 'קרח', month: 10, day: 13 },
    { id: 'chukat', name: 'Chukat', hebrewName: 'חוקת', month: 10, day: 20 },
    { id: 'balak', name: 'Balak', hebrewName: 'בלק', month: 10, day: 27 },
    { id: 'pinchas', name: 'Pinchas', hebrewName: 'פינחס', month: 11, day: 4 },
    { id: 'matot', name: 'Matot', hebrewName: 'מטות', month: 11, day: 11 },
    { id: 'massei', name: 'Massei', hebrewName: 'מסעי', month: 11, day: 18 },
    { id: 'devarim', name: 'Devarim', hebrewName: 'דברים', month: 11, day: 25 },
    { id: 'vaetchanan', name: 'Vaetchanan', hebrewName: 'ואתחנן', month: 12, day: 2 },
    { id: 'ekev', name: 'Ekev', hebrewName: 'עקב', month: 12, day: 9 },
    { id: 'reeh', name: 'Reeh', hebrewName: 'ראה', month: 12, day: 16 },
    { id: 'shoftim', name: 'Shoftim', hebrewName: 'שופטים', month: 12, day: 23 },
    { id: 'ki_tetze', name: 'Ki Tetze', hebrewName: 'כי תצא', month: 1, day: 1, nextYear: true },
    { id: 'ki_tavo', name: 'Ki Tavo', hebrewName: 'כי תבוא', month: 1, day: 8, nextYear: true },
    { id: 'nitzavim', name: 'Nitzavim', hebrewName: 'נצבים', month: 1, day: 15, nextYear: true },
    { id: 'vayelech', name: 'Vayelech', hebrewName: 'וילך', month: 1, day: 22, nextYear: true },
    { id: 'haazinu', name: 'Haazinu', hebrewName: 'האזינו', month: 1, day: 29, nextYear: true },
    { id: 'vezot_haberacha', name: 'Vezot Haberacha', hebrewName: 'וזאת הברכה', month: 2, day: 6, nextYear: true }
  ];

  const portions = basePortions.map(portion => {
    const actualYear = portion.nextYear ? year + 1 : year;
    const hebrewDay = numberToHebrew(portion.day);
    const hebrewYear = yearToHebrew(actualYear);
    // פורמט השנה: התש + כל האותיות חוץ מהאחרונה + " + האות האחרונה
    const yearBeforeLast = hebrewYear.slice(0, -1);
    const yearLast = hebrewYear.slice(-1);
    const hebrewDate = `${hebrewDay} ${getMonthName(portion.month)} התש${yearBeforeLast}"${yearLast}`;
    const gregorianDate = getGregorianDateForHebrew(portion.month, portion.day, actualYear);
    
    return {
      id: `${portion.id}_${year}`,
      name: portion.name,
      hebrewName: portion.hebrewName,
      hebrewDate,
      gregorianDate,
      year: actualYear
    };
  });

  // הוסף את הפרשה הכללית בתחילת הרשימה
  return [generalPortion, ...portions];
};

// פונקציה להמרת מספר לאותיות עבריות
const numberToHebrew = (num: number): string => {
  const hebrewNumbers = [
    '', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י',
    'יא', 'יב', 'יג', 'יד', 'טו', 'טז', 'יז', 'יח', 'יט', 'כ',
    'כא', 'כב', 'כג', 'כד', 'כה', 'כו', 'כז', 'כח', 'כט', 'ל'
  ];
  
  if (num <= 30) {
    return hebrewNumbers[num] || num.toString();
  }
  
  // למספרים גדולים מ-30, נחזיר את המספר הרגיל
  return num.toString();
};

// פונקציה להמרת שנה עברית לאותיות
const yearToHebrew = (year: number): string => {
  const yearStr = year.toString();
  const lastTwoDigits = parseInt(yearStr.slice(-2));
  
  // המרת שתי הספרות האחרונות לאותיות עבריות
  const tens = Math.floor(lastTwoDigits / 10);
  const ones = lastTwoDigits % 10;
  
  let hebrewYear = '';
  
  // עשרות
  if (tens === 1) {
    hebrewYear += 'י';
  } else if (tens === 2) {
    hebrewYear += 'כ';
  } else if (tens === 3) {
    hebrewYear += 'ל';
  } else if (tens === 4) {
    hebrewYear += 'מ';
  } else if (tens === 5) {
    hebrewYear += 'נ';
  } else if (tens === 6) {
    hebrewYear += 'ס';
  } else if (tens === 7) {
    hebrewYear += 'ע';
  } else if (tens === 8) {
    hebrewYear += 'פ';
  } else if (tens === 9) {
    hebrewYear += 'צ';
  }
  
  // יחידות
  if (ones === 1) {
    hebrewYear += 'א';
  } else if (ones === 2) {
    hebrewYear += 'ב';
  } else if (ones === 3) {
    hebrewYear += 'ג';
  } else if (ones === 4) {
    hebrewYear += 'ד';
  } else if (ones === 5) {
    hebrewYear += 'ה';
  } else if (ones === 6) {
    hebrewYear += 'ו';
  } else if (ones === 7) {
    hebrewYear += 'ז';
  } else if (ones === 8) {
    hebrewYear += 'ח';
  } else if (ones === 9) {
    hebrewYear += 'ט';
  }
  
  return hebrewYear || yearStr;
};

// פונקציה לקבלת שם חודש עברי
const getMonthName = (month: number): string => {
  const months = ['', 'תשרי', 'חשון', 'כסלו', 'טבת', 'שבט', 'אדר', 'ניסן', 'אייר', 'סיון', 'תמוז', 'אב', 'אלול'];
  return months[month] || '';
};

// פונקציה לקבלת תאריך לועזי (פישוט - בפועל צריך חישוב מדויק)
const getGregorianDateForHebrew = (month: number, day: number, year: number): string => {
  // זה פישוט - בפועל צריך חישוב מדויק של לוח עברי
  const baseDate = new Date(2025, 9, 15); // תאריך בסיס
  const daysOffset = (month - 1) * 30 + day - 15; // פישוט
  const targetDate = new Date(baseDate.getTime() + daysOffset * 24 * 60 * 60 * 1000);
  return targetDate.toISOString().split('T')[0];
};

// פרשות השנה הנוכחית (תשפ"ו)
export const TORAH_PORTIONS: TorahPortion[] = generateTorahPortions(5786);

// פונקציה לקבלת פרשות לשנה ספציפית
export const getTorahPortionsForYear = (year: number): TorahPortion[] => {
  return generateTorahPortions(year);
};

// חגים התשפ"ו (2025-2026) - מותאם לארץ ישראל
export const HOLIDAYS: Holiday[] = [
  // תשרי
  { id: 'rosh_hashana_1', name: 'Rosh Hashana Day 1', hebrewName: 'ראש השנה יום א\'', hebrewDate: 'א\' תשרי התשפ"ו', gregorianDate: '2025-09-22', year: 5786, hasSixthAliyah: false },
  { id: 'rosh_hashana_2', name: 'Rosh Hashana Day 2', hebrewName: 'ראש השנה יום ב\'', hebrewDate: 'ב\' תשרי התשפ"ו', gregorianDate: '2025-09-23', year: 5786, hasSixthAliyah: false },
  { id: 'tzom_gedalya', name: 'Tzom Gedalya', hebrewName: 'צום גדליה', hebrewDate: 'ג\' תשרי התשפ"ו', gregorianDate: '2025-09-24', year: 5786, hasSixthAliyah: false },
  { id: 'yom_kippur', name: 'Yom Kippur', hebrewName: 'יום כיפור', hebrewDate: 'י\' תשרי התשפ"ו', gregorianDate: '2025-10-01', year: 5786, hasSixthAliyah: true },
  { id: 'sukkot', name: 'Sukkot', hebrewName: 'סוכות', hebrewDate: 'ט"ו תשרי התשפ"ו', gregorianDate: '2025-10-07', year: 5786, hasSixthAliyah: false },
  { id: 'chol_hamoed_sukkot_1', name: 'Chol Hamoed Sukkot 1', hebrewName: 'חול המועד סוכות א\'', hebrewDate: 'י"ז תשרי התשפ"ו', gregorianDate: '2025-10-09', year: 5786, hasSixthAliyah: false },
  { id: 'chol_hamoed_sukkot_2', name: 'Chol Hamoed Sukkot 2', hebrewName: 'חול המועד סוכות ב\'', hebrewDate: 'י"ח תשרי התשפ"ו', gregorianDate: '2025-10-10', year: 5786, hasSixthAliyah: false },
  { id: 'chol_hamoed_sukkot_3', name: 'Chol Hamoed Sukkot 3', hebrewName: 'חול המועד סוכות ג\'', hebrewDate: 'י"ט תשרי התשפ"ו', gregorianDate: '2025-10-11', year: 5786, hasSixthAliyah: false },
  { id: 'chol_hamoed_sukkot_4', name: 'Chol Hamoed Sukkot 4', hebrewName: 'חול המועד סוכות ד\'', hebrewDate: 'כ\' תשרי התשפ"ו', gregorianDate: '2025-10-12', year: 5786, hasSixthAliyah: false },
  { id: 'hoshana_rabba', name: 'Hoshana Rabba', hebrewName: 'הושענא רבה', hebrewDate: 'כ"א תשרי התשפ"ו', gregorianDate: '2025-10-13', year: 5786, hasSixthAliyah: false },
  { id: 'shmini_atzeret', name: 'Shmini Atzeret', hebrewName: 'שמיני עצרת', hebrewDate: 'כ"ב תשרי התשפ"ו', gregorianDate: '2025-10-14', year: 5786, hasSixthAliyah: false },
  
  // כסלו
  { id: 'chanukah_1', name: 'Chanukah Day 1', hebrewName: 'חנוכה יום א\'', hebrewDate: 'כ"ה כסלו התשפ"ו', gregorianDate: '2025-12-15', year: 5786, hasSixthAliyah: false },
  { id: 'chanukah_2', name: 'Chanukah Day 2', hebrewName: 'חנוכה יום ב\'', hebrewDate: 'כ"ו כסלו התשפ"ו', gregorianDate: '2025-12-16', year: 5786, hasSixthAliyah: false },
  { id: 'chanukah_3', name: 'Chanukah Day 3', hebrewName: 'חנוכה יום ג\'', hebrewDate: 'כ"ז כסלו התשפ"ו', gregorianDate: '2025-12-17', year: 5786, hasSixthAliyah: false },
  { id: 'chanukah_4', name: 'Chanukah Day 4', hebrewName: 'חנוכה יום ד\'', hebrewDate: 'כ"ח כסלו התשפ"ו', gregorianDate: '2025-12-18', year: 5786, hasSixthAliyah: false },
  { id: 'chanukah_5', name: 'Chanukah Day 5', hebrewName: 'חנוכה יום ה\'', hebrewDate: 'כ"ט כסלו התשפ"ו', gregorianDate: '2025-12-19', year: 5786, hasSixthAliyah: false },
  { id: 'chanukah_6', name: 'Chanukah Day 6', hebrewName: 'חנוכה יום ו\'', hebrewDate: 'א\' טבת התשפ"ו', gregorianDate: '2025-12-20', year: 5786, hasSixthAliyah: false },
  { id: 'chanukah_7', name: 'Chanukah Day 7', hebrewName: 'חנוכה יום ז\'', hebrewDate: 'ב\' טבת התשפ"ו', gregorianDate: '2025-12-21', year: 5786, hasSixthAliyah: false },
  { id: 'chanukah_8', name: 'Chanukah Day 8', hebrewName: 'חנוכה יום ח\'', hebrewDate: 'ג\' טבת התשפ"ו', gregorianDate: '2025-12-22', year: 5786, hasSixthAliyah: false },
  
  // שבט
  { id: 'tu_bishvat', name: 'Tu Bishvat', hebrewName: 'ט"ו בשבט', hebrewDate: 'ט"ו שבט התשפ"ו', gregorianDate: '2026-02-03', year: 5786, hasSixthAliyah: false },
  
  // אדר
  { id: 'purim', name: 'Purim', hebrewName: 'פורים', hebrewDate: 'י"ד אדר התשפ"ו', gregorianDate: '2026-03-03', year: 5786, hasSixthAliyah: false },
  { id: 'shushan_purim', name: 'Shushan Purim', hebrewName: 'שושן פורים', hebrewDate: 'ט"ו אדר התשפ"ו', gregorianDate: '2026-03-04', year: 5786, hasSixthAliyah: false },
  
  // ניסן
  { id: 'pesach', name: 'Pesach', hebrewName: 'פסח', hebrewDate: 'ט"ו ניסן התשפ"ו', gregorianDate: '2026-04-01', year: 5786, hasSixthAliyah: false },
  { id: 'chol_hamoed_pesach_1', name: 'Chol Hamoed Pesach 1', hebrewName: 'חול המועד פסח א\'', hebrewDate: 'י"ז ניסן התשפ"ו', gregorianDate: '2026-04-03', year: 5786, hasSixthAliyah: false },
  { id: 'chol_hamoed_pesach_2', name: 'Chol Hamoed Pesach 2', hebrewName: 'חול המועד פסח ב\'', hebrewDate: 'י"ח ניסן התשפ"ו', gregorianDate: '2026-04-04', year: 5786, hasSixthAliyah: false },
  { id: 'chol_hamoed_pesach_3', name: 'Chol Hamoed Pesach 3', hebrewName: 'חול המועד פסח ג\'', hebrewDate: 'י"ט ניסן התשפ"ו', gregorianDate: '2026-04-05', year: 5786, hasSixthAliyah: false },
  { id: 'chol_hamoed_pesach_4', name: 'Chol Hamoed Pesach 4', hebrewName: 'חול המועד פסח ד\'', hebrewDate: 'כ\' ניסן התשפ"ו', gregorianDate: '2026-04-06', year: 5786, hasSixthAliyah: false },
  { id: 'pesach_7', name: 'Pesach Day 7', hebrewName: 'פסח יום ז\'', hebrewDate: 'כ"א ניסן התשפ"ו', gregorianDate: '2026-04-07', year: 5786, hasSixthAliyah: false },
  
  // אייר
  { id: 'yom_haatzmaut', name: 'Yom Haatzmaut', hebrewName: 'יום העצמאות', hebrewDate: 'ה\' אייר התשפ"ו', gregorianDate: '2026-04-23', year: 5786, hasSixthAliyah: false },
  { id: 'lag_baomer', name: 'Lag Baomer', hebrewName: 'ל"ג בעומר', hebrewDate: 'י"ח אייר התשפ"ו', gregorianDate: '2026-05-06', year: 5786, hasSixthAliyah: false },
  
  // סיון
  { id: 'shavuot', name: 'Shavuot', hebrewName: 'שבועות', hebrewDate: 'ו\' סיון התשפ"ו', gregorianDate: '2026-05-23', year: 5786, hasSixthAliyah: false },
  
  // תמוז
  { id: 'tzom_tammuz', name: 'Tzom Tammuz', hebrewName: 'צום תמוז', hebrewDate: 'י"ז תמוז התשפ"ו', gregorianDate: '2026-07-13', year: 5786, hasSixthAliyah: false },
  
  // אב
  { id: 'tisha_bav', name: 'Tisha B\'av', hebrewName: 'תשעה באב', hebrewDate: 'ט\' אב התשפ"ו', gregorianDate: '2026-08-03', year: 5786, hasSixthAliyah: false },
  { id: 'tu_bav', name: 'Tu B\'av', hebrewName: 'ט"ו באב', hebrewDate: 'ט"ו אב התשפ"ו', gregorianDate: '2026-08-09', year: 5786, hasSixthAliyah: false }
];

// סוגי תפילות
export const PRAYER_TYPES: PrayerType[] = [
  {
    id: 'shacharit_early',
    name: 'Shacharit Early',
    hebrewName: 'שחרית נץ',
    services: [
      { id: 'open_heichal', name: 'Open Heichal', hebrewName: 'פתיחת ההיכל', suggestedPrice: 50 },
      { id: 'hagbah', name: 'Hagbah', hebrewName: 'הגבהת ספר תורה', suggestedPrice: 100 },
      { id: 'gelilah', name: 'Gelilah', hebrewName: 'הולכת ספר תורה', suggestedPrice: 50 },
      { id: 'aliyah_1', name: 'Aliyah 1', hebrewName: 'עליה ראשונה', suggestedPrice: 200 },
      { id: 'aliyah_2', name: 'Aliyah 2', hebrewName: 'עליה שנייה', suggestedPrice: 200 },
      { id: 'aliyah_3', name: 'Aliyah 3', hebrewName: 'עליה שלישית', suggestedPrice: 200 },
      { id: 'aliyah_4', name: 'Aliyah 4', hebrewName: 'עליה רביעית', suggestedPrice: 200 },
      { id: 'aliyah_5', name: 'Aliyah 5', hebrewName: 'עליה חמישית', suggestedPrice: 200 },
      { id: 'aliyah_6', name: 'Aliyah 6', hebrewName: 'עליה שישית', suggestedPrice: 200 },
      { id: 'aliyah_7', name: 'Aliyah 7', hebrewName: 'עליה שביעית', suggestedPrice: 200 },
      { id: 'haftarah', name: 'Haftarah', hebrewName: 'הפטרה', suggestedPrice: 300 }
    ]
  },
  {
    id: 'shacharit_regular',
    name: 'Shacharit Regular',
    hebrewName: 'שחרית רגיל',
    services: [
      { id: 'open_heichal', name: 'Open Heichal', hebrewName: 'פתיחת ההיכל', suggestedPrice: 50 },
      { id: 'hagbah', name: 'Hagbah', hebrewName: 'הגבהת ספר תורה', suggestedPrice: 100 },
      { id: 'gelilah', name: 'Gelilah', hebrewName: 'הולכת ספר תורה', suggestedPrice: 50 },
      { id: 'aliyah_1', name: 'Aliyah 1', hebrewName: 'עליה ראשונה', suggestedPrice: 200 },
      { id: 'aliyah_2', name: 'Aliyah 2', hebrewName: 'עליה שנייה', suggestedPrice: 200 },
      { id: 'aliyah_3', name: 'Aliyah 3', hebrewName: 'עליה שלישית', suggestedPrice: 200 },
      { id: 'aliyah_4', name: 'Aliyah 4', hebrewName: 'עליה רביעית', suggestedPrice: 200 },
      { id: 'aliyah_5', name: 'Aliyah 5', hebrewName: 'עליה חמישית', suggestedPrice: 200 },
      { id: 'aliyah_6', name: 'Aliyah 6', hebrewName: 'עליה שישית', suggestedPrice: 200 },
      { id: 'aliyah_7', name: 'Aliyah 7', hebrewName: 'עליה שביעית', suggestedPrice: 200 },
      { id: 'haftarah', name: 'Haftarah', hebrewName: 'הפטרה', suggestedPrice: 300 }
    ]
  },
  {
    id: 'mincha',
    name: 'Mincha',
    hebrewName: 'מנחה',
    services: [
      { id: 'open_heichal', name: 'Open Heichal', hebrewName: 'פתיחת ההיכל', suggestedPrice: 50 },
      { id: 'hagbah', name: 'Hagbah', hebrewName: 'הגבהת ספר תורה', suggestedPrice: 100 },
      { id: 'gelilah', name: 'Gelilah', hebrewName: 'הולכת ספר תורה', suggestedPrice: 50 },
      { id: 'aliyah_1', name: 'Aliyah 1', hebrewName: 'עליה ראשונה', suggestedPrice: 200 },
      { id: 'aliyah_2', name: 'Aliyah 2', hebrewName: 'עליה שנייה', suggestedPrice: 200 },
      { id: 'aliyah_3', name: 'Aliyah 3', hebrewName: 'עליה שלישית', suggestedPrice: 200 }
    ]
  },
  {
    id: 'other',
    name: 'Other',
    hebrewName: 'אחר',
    services: [
      { id: 'general_service', name: 'General Service', hebrewName: 'שירות כללי', suggestedPrice: 0 }
    ]
  }
];

// פונקציה לקבלת שירותים לפי סוג תפילה וחג
export const getServicesForPrayer = (prayerTypeId: string, isHoliday: boolean = false, holidayId?: string): Service[] => {
  const prayerType = PRAYER_TYPES.find(p => p.id === prayerTypeId);
  if (!prayerType) return [];

  let services = [...prayerType.services];

  // אם זה יום כיפור, הוסף עליה שישית לשחרית
  if (isHoliday && holidayId === 'yom_kippur' && (prayerTypeId === 'shacharit_early' || prayerTypeId === 'shacharit_regular')) {
    services = services.filter(s => s.id !== 'aliyah_6'); // הסר את העליה השישית הקיימת
    services.splice(5, 0, { id: 'aliyah_6', name: 'Aliyah 6', hebrewName: 'עליה שישית', suggestedPrice: 200 }); // הוסף אותה במקום הנכון
  }

  // אם זה חג (לא שבת), הסר עליות 6-7 משחרית
  if (isHoliday && (prayerTypeId === 'shacharit_early' || prayerTypeId === 'shacharit_regular')) {
    services = services.filter(s => !['aliyah_6', 'aliyah_7'].includes(s.id));
  }

  return services;
};

// פונקציה להמרת תאריך עברי (לייצוא לשימוש כללי)
export const convertToHebrewDate = (date: Date): string => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  
  // המרת מספר לאותיות עבריות
  const hebrewNumbers = [
    '', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י',
    'יא', 'יב', 'יג', 'יד', 'טו', 'טז', 'יז', 'יח', 'יט', 'כ',
    'כא', 'כב', 'כג', 'כד', 'כה', 'כו', 'כז', 'כח', 'כט', 'ל'
  ];
  const hebrewDay = hebrewNumbers[day] || day.toString();
  
  // חודשים עבריים
  const hebrewMonths = ['', 'תשרי', 'חשון', 'כסלו', 'טבת', 'שבט', 'אדר', 'ניסן', 'אייר', 'סיון', 'תמוז', 'אב', 'אלול'];
  const hebrewMonth = hebrewMonths[month] || 'תשרי';
  
  // המרת שנה עברית לאותיות
  const hebrewYear = yearToHebrew(year);
  
  // פורמט השנה: התש + כל האותיות חוץ מהאחרונה + " + האות האחרונה
  const yearBeforeLast = hebrewYear.slice(0, -1);
  const yearLast = hebrewYear.slice(-1);
  
  return `${hebrewDay} ${hebrewMonth} התש${yearBeforeLast}"${yearLast}`;
};

// פונקציה לקבלת תאריך לועזי מפרשה או חג
export const getGregorianDate = (torahPortionId?: string, holidayId?: string): string => {
  if (torahPortionId) {
    const portion = TORAH_PORTIONS.find(p => p.id === torahPortionId);
    return portion?.gregorianDate || '';
  }
  if (holidayId) {
    const holiday = HOLIDAYS.find(h => h.id === holidayId);
    return holiday?.gregorianDate || '';
  }
  return '';
};